import { apiPathUrl } from './config';

const TENANT_PROVIDER_KEY = 'tenantProvider';
const TENANT_APPS = ['user_module', 'booking', 'service_provider', 'mobile_side', 'email_marketing'];
const PROVIDER_FREE_PATHS = new Set([
  '/user_module/user_login',
  '/user_module/mobile_login',
  '/user_module/forget_password',
  '/user_module/reset_password',
  '/user_module/reset_password_admin',
]);
const OPTIONAL_PROVIDER_PATHS = new Set([
  '/user_module/user_email_signup',
  '/user_module/user_information_signup',
]);

const readUserProvider = () => {
  try {
    const user = JSON.parse(window.localStorage.getItem('user'));
    return user?.provider || null;
  } catch (error) {
    return null;
  }
};

export const getActiveProvider = () => {
  const pathMatch = window.location.pathname.match(/^\/provider\/([^/]+)\/(?:services|booking|confirm-booking|signup|profile|zelle-checkout)(?:\/|$)/)
    || window.location.pathname.match(/^\/(?:booking|services)\/provider\/([^/]+)(?:\/|$)/);
  if (pathMatch?.[1]) {
    const pathProvider = decodeURIComponent(pathMatch[1]);
    window.localStorage.setItem(TENANT_PROVIDER_KEY, pathProvider);
    return pathProvider;
  }

  const queryProvider = new URLSearchParams(window.location.search).get('provider');
  if (queryProvider) {
    window.localStorage.setItem(TENANT_PROVIDER_KEY, queryProvider);
    return queryProvider;
  }

  const userProvider = readUserProvider();
  if (userProvider) return userProvider;

  const storedProvider = window.localStorage.getItem(TENANT_PROVIDER_KEY);
  if (storedProvider) return storedProvider;

  return null;
};

export const saveActiveProvider = (provider) => {
  if (!provider) throw new Error('Login response is missing the company provider identifier.');
  window.localStorage.setItem(TENANT_PROVIDER_KEY, provider);
};

export const clearActiveProvider = () => {
  window.localStorage.removeItem(TENANT_PROVIDER_KEY);
};

export const tenantApi = (provider, app, endpoint = '') => {
  if (!provider) throw new Error('Missing company provider identifier.');
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  return `/${app}/provider/${encodeURIComponent(provider)}/${cleanEndpoint}`;
};

export const withTenantProvider = (requestUrl) => {
  if (!requestUrl || typeof requestUrl !== 'string') return requestUrl;

  const isAbsolute = /^https?:\/\//i.test(requestUrl);
  const parsed = new URL(requestUrl, apiPathUrl);
  const path = parsed.pathname;

  if (PROVIDER_FREE_PATHS.has(path) || path.startsWith('/admin/')) return requestUrl;

  const app = TENANT_APPS.find((name) => path.startsWith(`/${name}/`));
  if (!app || path.startsWith(`/${app}/provider/`)) return requestUrl;

  const provider = getActiveProvider();
  if (!provider && OPTIONAL_PROVIDER_PATHS.has(path)) return requestUrl;
  const endpoint = path.slice(app.length + 2);
  parsed.pathname = tenantApi(provider, app, endpoint);

  return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const tenantWebSocketPath = (endpoint = '') => {
  const provider = getActiveProvider();
  if (!provider) throw new Error('Missing company provider identifier.');
  return `/ws/provider/${encodeURIComponent(provider)}/${endpoint.replace(/^\/+/, '')}`;
};

export const tenantBookingChatWebSocketPath = (bookingId) => {
  if (!bookingId) throw new Error('Missing booking identifier.');
  return tenantWebSocketPath(`chat/${encodeURIComponent(bookingId)}`);
};

export const tenantPageUrl = (path) => {
  const provider = getActiveProvider();
  if (!provider) throw new Error('Missing company provider identifier.');
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}provider=${encodeURIComponent(provider)}`;
};

export const tenantBookingPageUrl = (slug) => {
  const provider = getActiveProvider();
  if (!provider) throw new Error('Missing company provider identifier.');
  return `/provider/${encodeURIComponent(provider)}/booking/${encodeURIComponent(slug || '')}`;
};

export const tenantServicesPageUrl = () => {
  const provider = getActiveProvider();
  if (!provider) throw new Error('Missing company provider identifier.');
  return `/provider/${encodeURIComponent(provider)}/services`;
};

export const tenantPublicPageUrl = (page) => {
  const provider = getActiveProvider();
  if (!provider) throw new Error('Missing company provider identifier.');
  return `/provider/${encodeURIComponent(provider)}/${page.replace(/^\/+/, '')}`;
};
